/**
 * Utility functions for creating and parsing SEO-friendly URL slugs
 */

/**
 * Create a URL-safe slug from a name
 */
export const createSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[áäàâã]/g, 'a')
    .replace(/[éěë]/g, 'e')
    .replace(/[íî]/g, 'i')
    .replace(/[óöôõ]/g, 'o')
    .replace(/[úůüû]/g, 'u')
    .replace(/[ýÿ]/g, 'y')
    .replace(/[ñ]/g, 'n')
    .replace(/[ç]/g, 'c')
    .replace(/[š]/g, 's')
    .replace(/[č]/g, 'c')
    .replace(/[ř]/g, 'r')
    .replace(/[ž]/g, 'z')
    .replace(/[ť]/g, 't')
    .replace(/[ď]/g, 'd')
    .replace(/[ň]/g, 'n')
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim()
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Extract ID from a slug that contains both ID and name (format: "id-name-slug")
 */
export const extractIdFromSlug = (slug: string): string => {
  // UUID format: 8-4-4-4-12 characters (total 36 chars with hyphens)
  // Extract the first 36 characters which should be the UUID
  const uuidMatch = slug.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
  return uuidMatch ? uuidMatch[0] : slug.split('-')[0];
};

/**
 * Create a combined slug with ID and name for easier parsing
 */
export const createIdSlug = (id: string, name: string): string => {
  const nameSlug = createSlug(name);
  return `${id}-${nameSlug}`;
};

/**
 * Create race slug from race data
 */
export const createRaceSlug = (eventId: string, raceId: string, raceNumber: number, location: string, time: string): string => {
  const locationSlug = createSlug(location);
  const timeSlug = createSlug(time);
  return `${eventId}-${raceId}-${raceNumber}-dostih-${locationSlug}-${timeSlug}`;
};

/**
 * Parse race slug to extract event and race IDs
 */
export const parseRaceSlug = (slug: string): { eventId: string; raceId: string } => {
  // Extract first UUID (eventId) - first 36 characters
  const eventIdMatch = slug.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
  const eventId = eventIdMatch ? eventIdMatch[0] : '';
  
  // Extract second UUID (raceId) - starts after first UUID and a hyphen
  const afterEventId = slug.substring(eventId.length + 1); // +1 for the separating hyphen
  const raceIdMatch = afterEventId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
  const raceId = raceIdMatch ? raceIdMatch[0] : '';
  
  return {
    eventId,
    raceId
  };
};

/**
 * Create a URL-safe slug for a product
 */
export const createProductSlug = (id: string, name: string): string => {
  const nameSlug = createSlug(name);
  return `${id}-${nameSlug}`;
};

/**
 * Extract product ID from a product slug
 */
export const extractProductIdFromSlug = (slug: string): string => {
  return extractIdFromSlug(slug);
};